import React from 'react'
import PropTypes from 'prop-types'
import { Button, Spin, Tag, Collapse, message } from 'antd'
import { ArrowLeftOutlined, SaveOutlined, MenuOutlined } from '@ant-design/icons'
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc'
import {
  getRamsDocumentOrder,
  saveRamsDocumentOrder,
} from '../../../../HttpRequests/ramsControlCentre'

var Panel = Collapse.Panel

var DOCUMENT_TYPE_LABELS = {
  revision: 'Revision',
  signed: 'Signed',
  header: 'Header',
  footer: 'Footer',
  service: 'Service',
  access: 'Equipment',
  paint: 'Paint',
  ppe: 'PPE',
  task: 'Task',
  general: 'General',
}

var DOCUMENT_TYPE_COLORS = {
  revision: 'purple',
  signed: 'cyan',
  header: 'blue',
  footer: 'blue',
  service: 'green',
  access: 'orange',
  paint: 'magenta',
  ppe: 'red',
  task: 'gold',
  general: 'default',
}

var DOC_ORDER_SECTIONS = [
  { key: 'rams_service_doc_order', type: 'service', label: 'Service Documents' },
  { key: 'rams_access_doc_order', type: 'access', label: 'Equipment Documents' },
  { key: 'rams_paint_doc_order', type: 'paint', label: 'Paint Documents' },
  { key: 'rams_ppe_doc_order', type: 'ppe', label: 'PPE Documents' },
  { key: 'rams_task_doc_order', type: 'task', label: 'Task Documents' },
  { key: 'rams_general_doc_order', type: 'general', label: 'General Documents' },
]

var DragHandle = SortableHandle(function() {
  return (
    <MenuOutlined style={{ cursor: 'grab', color: '#999', marginRight: 12, fontSize: 14 }} />
  )
})

var SortableItem = SortableElement(function(props) {
  var item = props.item
  var color = props.color || 'default'
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      padding: '10px 14px',
      marginBottom: 4,
      background: '#fff',
      border: '1px solid #e8e8e8',
      borderRadius: 4,
      userSelect: 'none',
      zIndex: 9999,
    }}>
      <DragHandle />
      <Tag color={color} style={{ marginRight: 10, marginBottom: 0 }}>{item.tag || ''}</Tag>
      <span style={{ flex: 1 }}>{item.label}</span>
    </div>
  )
})

var SortableList = SortableContainer(function(props) {
  var items = props.items
  var color = props.color || 'default'
  return (
    <div>
      {items.map(function(item, index) {
        return (
          <SortableItem
            key={item.id || item.value || index}
            index={index}
            item={item}
            color={color}
          />
        )
      })}
      {items.length === 0 && (
        <div style={{ padding: '16px', color: '#999', textAlign: 'center', fontStyle: 'italic' }}>
          No documents in this section
        </div>
      )}
    </div>
  )
})

function arrayMove(arr, fromIndex, toIndex) {
  var newArr = arr.slice()
  var element = newArr.splice(fromIndex, 1)[0]
  newArr.splice(toIndex, 0, element)
  return newArr
}

export default class DocumentOrderEditor extends React.PureComponent {

  static propTypes = {
    documents: PropTypes.array.isRequired,
    serviceTypes: PropTypes.array,
    userSettings: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    onBack: PropTypes.func.isRequired,
  }

  state = {
    loading: true,
    saving: false,
    documentTypeOrder: [],
    rams_service_doc_order: [],
    rams_access_doc_order: [],
    rams_paint_doc_order: [],
    rams_ppe_doc_order: [],
    rams_task_doc_order: [],
    rams_general_doc_order: [],
    dirty: false,
  }

  componentDidMount() {
    this.loadOrder()
  }

  loadOrder = () => {
    var self = this
    self.setState({ loading: true })

    getRamsDocumentOrder()
      .then(function(response) {
        var data = response.data.data || {}

        var typeOrder = data.rams_document_type_order
        if (!typeOrder || typeOrder.length === 0) {
          typeOrder = ['revision', 'signed', 'header', 'footer', 'service', 'access', 'paint', 'ppe', 'task', 'general']
        }
        // Ensure any new types added since the setting was saved are included
        var allKnownTypes = ['revision', 'signed', 'header', 'footer', 'service', 'access', 'paint', 'ppe', 'task', 'general']
        allKnownTypes.forEach(function(t) {
          if (typeOrder.indexOf(t) === -1) {
            typeOrder.push(t)
          }
        })

        self.setState({
          loading: false,
          documentTypeOrder: typeOrder,
          rams_service_doc_order: data.rams_service_doc_order || [],
          rams_access_doc_order: data.rams_access_doc_order || [],
          rams_paint_doc_order: data.rams_paint_doc_order || [],
          rams_ppe_doc_order: data.rams_ppe_doc_order || [],
          rams_task_doc_order: data.rams_task_doc_order || [],
          rams_general_doc_order: data.rams_general_doc_order || [],
          dirty: false,
        })
      })
      .catch(function() {
        self.setState({ loading: false })
        message.error('Failed to load document order settings')
      })
  }

  handleSave = () => {
    var self = this
    self.setState({ saving: true })

    var orders = {
      rams_document_type_order: self.state.documentTypeOrder,
      rams_service_doc_order: self.state.rams_service_doc_order,
      rams_access_doc_order: self.state.rams_access_doc_order,
      rams_paint_doc_order: self.state.rams_paint_doc_order,
      rams_ppe_doc_order: self.state.rams_ppe_doc_order,
      rams_task_doc_order: self.state.rams_task_doc_order,
      rams_general_doc_order: self.state.rams_general_doc_order,
    }

    saveRamsDocumentOrder(orders)
      .then(function() {
        self.setState({ saving: false, dirty: false })
        message.success('Document order saved')
      })
      .catch(function() {
        self.setState({ saving: false })
        message.error('Failed to save document order')
      })
  }

  handleDocumentTypeSort = (result) => {
    var oldIndex = result.oldIndex
    var newIndex = result.newIndex
    if (oldIndex === newIndex) return
    this.setState(function(prevState) {
      return {
        documentTypeOrder: arrayMove(prevState.documentTypeOrder, oldIndex, newIndex),
        dirty: true,
      }
    })
  }

  handleDocSort = (stateKey) => {
    var self = this
    return function(result) {
      var oldIndex = result.oldIndex
      var newIndex = result.newIndex
      if (oldIndex === newIndex) return
      self.setState(function(prevState) {
        var update = { dirty: true }
        update[stateKey] = arrayMove(prevState[stateKey], oldIndex, newIndex)
        return update
      })
    }
  }

  getDocumentTypeItems = () => {
    var order = this.state.documentTypeOrder
    return order.map(function(typeKey) {
      return {
        id: typeKey,
        value: typeKey,
        label: DOCUMENT_TYPE_LABELS[typeKey] || typeKey,
        tag: DOCUMENT_TYPE_LABELS[typeKey] || typeKey,
      }
    })
  }

  getDocItemsForSection = (sectionKey, docType) => {
    var documents = this.props.documents || []
    var savedOrder = this.state[sectionKey] || []
    var serviceTypes = this.props.serviceTypes || []
    var userSettings = this.props.userSettings || {}

    var docsOfType = documents.filter(function(doc) {
      return doc.document_type === docType && doc.active
    })

    var docMap = {}
    docsOfType.forEach(function(doc) {
      docMap[doc.id] = doc
    })

    var orderedDocs = []
    savedOrder.forEach(function(id) {
      if (docMap[id]) {
        orderedDocs.push(docMap[id])
        delete docMap[id]
      }
    })

    Object.keys(docMap).forEach(function(id) {
      orderedDocs.push(docMap[id])
    })

    return orderedDocs.map(function(doc) {
      var subtitle = ''
      var typeCodes = Array.isArray(doc.type_code) ? doc.type_code : (doc.type_code ? [doc.type_code] : [])

      if (typeCodes.length > 0) {
        if (docType === 'service') {
          var labels = typeCodes.map(function(code) {
            var found = serviceTypes.find(function(st) { return st.key === code })
            return found ? (code + ' - ' + found.title) : code
          })
          subtitle = ' (' + labels.join(', ') + ')'
        } else if (docType === 'access') {
          var accessItems = userSettings.access || []
          var accessLabels = typeCodes.map(function(code) {
            var found = accessItems.find(function(item) { return item.key === code })
            return found ? found.value : code
          })
          subtitle = ' (' + accessLabels.join(', ') + ')'
        } else if (docType === 'paint') {
          subtitle = ' (' + typeCodes.join(', ') + ')'
        } else if (docType === 'ppe') {
          var ppeItems = userSettings.ppe || []
          var ppeLabels = typeCodes.map(function(code) {
            var found = ppeItems.find(function(item) { return item.key === code })
            return found ? found.value : code
          })
          subtitle = ' (' + ppeLabels.join(', ') + ')'
        } else if (docType === 'task') {
          var taskItems = userSettings.tasks || []
          var taskLabels = typeCodes.map(function(code) {
            var found = taskItems.find(function(item) { return item.key === code })
            return found ? found.value : code
          })
          subtitle = ' (' + taskLabels.join(', ') + ')'
        }
      }

      return {
        id: doc.id,
        value: doc.id,
        label: doc.title + subtitle,
        tag: 'v' + (doc.version || 1),
      }
    })
  }

  handleDocSectionSort = (sectionKey) => {
    var self = this
    return function(result) {
      var oldIndex = result.oldIndex
      var newIndex = result.newIndex
      if (oldIndex === newIndex) return

      self.setState(function(prevState) {
        var section = DOC_ORDER_SECTIONS.find(function(s) { return s.key === sectionKey })
        var docType = section ? section.type : null
        var documents = self.props.documents || []
        var savedOrder = prevState[sectionKey] || []

        var docsOfType = documents.filter(function(doc) {
          return doc.document_type === docType && doc.active
        })
        var docMap = {}
        docsOfType.forEach(function(doc) { docMap[doc.id] = doc })

        var currentOrder = []
        savedOrder.forEach(function(id) {
          if (docMap[id]) {
            currentOrder.push(id)
            delete docMap[id]
          }
        })
        Object.keys(docMap).forEach(function(id) {
          currentOrder.push(parseInt(id))
        })

        var newOrder = arrayMove(currentOrder, oldIndex, newIndex)
        var update = { dirty: true }
        update[sectionKey] = newOrder
        return update
      })
    }
  }

  render() {
    var self = this
    var loading = this.state.loading
    var saving = this.state.saving
    var dirty = this.state.dirty

    if (loading) {
      return (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <Spin size="large" />
          <div style={{ marginTop: 16, color: '#999' }}>Loading document order settings...</div>
        </div>
      )
    }

    var docTypeItems = this.getDocumentTypeItems()

    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 20 }}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={this.props.onBack}
            size="small"
            style={{ marginRight: 12 }}
          >
            Back to Documents
          </Button>
          <span style={{ flex: 1, fontWeight: 'bold', fontSize: 15 }}>
            Document Order Configuration
          </span>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={this.handleSave}
            loading={saving}
            disabled={!dirty}
          >
            Save Order
          </Button>
        </div>

        <div style={{ marginBottom: 8, color: '#666', fontSize: 13 }}>
          Drag items to reorder. Changes take effect when you click Save Order.
        </div>

        <Collapse defaultActiveKey={['document_type_order']} bordered={false}>

          <Panel
            header={<span style={{ fontWeight: 'bold' }}>Document Type Order</span>}
            key="document_type_order"
          >
            <div style={{ marginBottom: 8, color: '#888', fontSize: 12 }}>
              Controls the order of document sections in the final RAMS output.
            </div>
            <SortableList
              items={docTypeItems}
              onSortEnd={this.handleDocumentTypeSort}
              useDragHandle
              helperClass="sortable-helper"
            />
          </Panel>

          {DOC_ORDER_SECTIONS.map(function(section) {
            var items = self.getDocItemsForSection(section.key, section.type)
            var color = DOCUMENT_TYPE_COLORS[section.type] || 'default'
            return (
              <Panel
                header={
                  <span style={{ fontWeight: 'bold' }}>
                    {section.label}
                    <Tag color={color} style={{ marginLeft: 8 }}>{items.length}</Tag>
                  </span>
                }
                key={section.key}
              >
                <SortableList
                  items={items}
                  color={color}
                  onSortEnd={self.handleDocSectionSort(section.key)}
                  useDragHandle
                  helperClass="sortable-helper"
                />
              </Panel>
            )
          })}

        </Collapse>
      </div>
    )
  }
}
